// src/components/work_orders/QuickWorkOrderModal.js
// FlowSense MOW+ — create a work order without leaving the page (Live Readings: wells, flood alerts,
// sensors) — NEW FILE
//   • Pre-filled from a FlowSense draft (wellIntelService.build*WorkOrderDraft): catalog domain / type /
//     category, priority, links (asset, well, sensor), location and the AI evidence in the description.
//   • Checks before creating: open jobs already on the same well / sensor (from the page + the backend
//     duplicate check), the catalog's hard rules (validateForCreate) and a due date where the type needs one.
//   • Creates through POST /workorders; the AI dispatcher assigns a technician as usual.
//   • "Open full form" hands the same draft to WorkOrderPage for anything this form does not cover.
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, FormControl, FormLabel, FormHelperText, Input, Select, Textarea, SimpleGrid, HStack, VStack, Text,
  Badge, Alert, AlertIcon, AlertDescription, Box, Icon, Spinner, useColorModeValue,
} from '@chakra-ui/react';
import { MdBuild, MdOpenInNew, MdWarning, MdPlaylistAddCheck } from 'react-icons/md';
import { PRIORITIES, WORK_ORDER_TYPES, getDomain, typesFor, categoriesFor, validateForCreate } from './workOrderCatalog';
import { createWorkOrder, describeWorkOrderError, fetchWorkOrderHelp, isOpenWorkOrder } from '../../services/liveWorkOrderService';

const PRIORITY_SCHEME = { low: 'gray', medium: 'blue', high: 'orange', critical: 'red' };
const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const defaultDue = (type, priority) => (WORK_ORDER_TYPES[type]?.requiresDueDate ? todayPlus(['high', 'critical'].includes(priority) ? 2 : 7) : '');
// --- UPDATE (v1.1): the page renders this closed with draft={null}; a `= {}` default only covers undefined
const formFrom = (draft) => {
  const d = draft && typeof draft === 'object' ? draft : {};
  return {
  title: d.title || '',
  priority: PRIORITIES.includes(d.priority) ? d.priority : 'medium',
  work_order_type: d.work_order_type || '',
  category: d.category || '',
  due_date: d.due_date ? String(d.due_date).slice(0, 10) : defaultDue(d.work_order_type, d.priority),
  description: d.description || '',
  };
};
// --- END UPDATE

function QuickWorkOrderModal({ isOpen, onClose, draft, openWorkOrders = [], onCreated, onOpenWorkOrder, onOpenFullForm }) {
  const muted = useColorModeValue('gray.500', 'gray.400');
  const panel = useColorModeValue('gray.50', 'whiteAlpha.50');
  const [form, setForm] = useState(() => formFrom(draft));
  const [help, setHelp] = useState(null);
  const [helpLoading, setHelpLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const busyRef = useRef(false);

  // reset for every new draft; ask the backend for duplicates + checklist (best effort)
  useEffect(() => {
    if (!isOpen || !draft) return undefined;
    setForm(formFrom(draft));
    setHelp(null);
    setError(null);
    setConfirmDuplicate(false);
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    setHelpLoading(true);
    fetchWorkOrderHelp(draft, { signal: ctrl?.signal }).then((h) => {
      if (ctrl?.signal.aborted) return;
      setHelp(h);
      setHelpLoading(false);
    });
    return () => ctrl?.abort();
  }, [isOpen, draft]);

  const domain = getDomain(draft?.domain);
  const typeOptions = useMemo(() => typesFor(draft?.domain), [draft?.domain]);
  const categoryOptions = useMemo(() => categoriesFor(draft?.domain), [draft?.domain]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // open jobs already on this well / sensor: the page's list + the backend duplicate check, once each
  const duplicates = useMemo(() => {
    const seen = new Map();
    [...(openWorkOrders || []), ...((help && help.duplicates) || [])].forEach((w) => {
      if (w && w.id != null && isOpenWorkOrder(w) && !seen.has(String(w.id))) seen.set(String(w.id), w);
    });
    return [...seen.values()];
  }, [openWorkOrders, help]);
  const sameJob = useMemo(() => {
    const t = form.title.trim().toLowerCase();
    return t ? duplicates.find((w) => String(w.title || '').trim().toLowerCase() === t) || null : null;
  }, [duplicates, form.title]);

  const merged = useMemo(() => ({ ...(draft || {}), ...form, category: form.category || undefined, due_date: form.due_date || undefined }), [draft, form]);
  const problems = useMemo(() => {
    const list = [];
    if (!form.title.trim()) list.push('Give the work order a title.');
    if (form.title.length > 500) list.push('The title is too long (max 500 characters).');
    if (form.description.length > 5000) list.push('The description is too long (max 5000 characters).');
    list.push(...validateForCreate(merged));
    return list;
  }, [form.title, form.description, merged]);
  const needsDue = !!WORK_ORDER_TYPES[form.work_order_type]?.requiresDueDate && !form.due_date;
  const checklist = (help && help.crew_checklist && help.crew_checklist.length ? help.crew_checklist : (categoryOptions.find((c) => c.label === form.category) || {}).checklist) || [];

  const submit = async () => {
    if (busyRef.current || problems.length) return;
    if (sameJob && !confirmDuplicate) { setConfirmDuplicate(true); return; }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const wo = await createWorkOrder(merged);
      onCreated?.(wo, merged);
    } catch (e) {
      setError(describeWorkOrderError(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const links = [
    draft?.well_id && ['Well', draft._entityName && draft.domain === 'well' ? `${draft._entityName} (${draft.well_id})` : draft.well_id],
    draft?.sensor_id && ['Sensor', draft.sensor_id],
    draft?.asset_id && draft.asset_id !== draft.well_id && ['Asset', draft.asset_id],
    draft?.location_description && ['Location', draft.location_description],
  ].filter(Boolean);

  return (
    <Modal isOpen={isOpen && !!draft} onClose={busy ? () => {} : onClose} size="xl" scrollBehavior="inside" closeOnOverlayClick={!busy}>
      <ModalOverlay backdropFilter="blur(3px)" />
      <ModalContent borderRadius="2xl">
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={MdBuild} color="purple.400" />
            <Text>New work order</Text>
            {domain && <Badge colorScheme={domain.color || 'purple'} variant="subtle">{domain.short || domain.label}</Badge>}
            {draft?.source && draft.source !== 'manual' && <Badge variant="outline" colorScheme="purple">FlowSense AI</Badge>}
          </HStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {links.length > 0 && (
              <Box p={3} bg={panel} borderRadius="lg" fontSize="sm">
                {links.map(([k, v]) => (
                  <HStack key={k} spacing={2}><Text color={muted} minW="70px">{k}</Text><Text fontWeight="600" noOfLines={1}>{v}</Text></HStack>
                ))}
              </Box>
            )}

            {duplicates.length > 0 && (
              <Alert status={sameJob ? 'warning' : 'info'} borderRadius="lg" alignItems="start" fontSize="sm">
                <AlertIcon mt={0.5} />
                <VStack align="stretch" spacing={1} flex="1">
                  <Text fontWeight="700">
                    {sameJob ? 'This job is already open' : `${duplicates.length} open work order${duplicates.length > 1 ? 's' : ''} on this ${domain?.short?.toLowerCase() || 'record'}`}
                  </Text>
                  {duplicates.slice(0, 5).map((w) => (
                    <HStack key={String(w.id)} justify="space-between">
                      <Text noOfLines={1}>{w.title || w.id} <Text as="span" color={muted}>· {w.status || 'open'}</Text></Text>
                      {onOpenWorkOrder && <Button size="xs" variant="ghost" rightIcon={<MdOpenInNew />} onClick={() => onOpenWorkOrder(w)}>Open</Button>}
                    </HStack>
                  ))}
                </VStack>
              </Alert>
            )}

            <FormControl isRequired isInvalid={!form.title.trim()}>
              <FormLabel fontSize="sm">Title</FormLabel>
              <Input value={form.title} onChange={set('title')} maxLength={500} />
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel fontSize="sm">Priority</FormLabel>
                <Select value={form.priority} onChange={set('priority')}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Type</FormLabel>
                <Select
                  value={form.work_order_type}
                  onChange={(e) => {
                    const t = e.target.value;
                    setForm((f) => ({ ...f, work_order_type: t, due_date: f.due_date || defaultDue(t, f.priority) }));
                  }}
                >
                  {!form.work_order_type && <option value="">Choose…</option>}
                  {typeOptions.map((t) => <option key={t} value={t}>{WORK_ORDER_TYPES[t]?.label || t}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Category</FormLabel>
                <Select value={form.category} onChange={set('category')}>
                  <option value="">Default for the type</option>
                  {form.category && !categoryOptions.some((c) => c.label === form.category) && <option value={form.category}>{form.category}</option>}
                  {categoryOptions.map((c) => <option key={c.label} value={c.label}>{c.label}</option>)}
                </Select>
              </FormControl>
              <FormControl isInvalid={needsDue}>
                <FormLabel fontSize="sm">Due date</FormLabel>
                <Input type="date" value={form.due_date} onChange={set('due_date')} min={todayPlus(0)} />
                {needsDue && <FormHelperText color="orange.400">{WORK_ORDER_TYPES[form.work_order_type]?.short || 'This'} work needs a due date.</FormHelperText>}
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel fontSize="sm">Description</FormLabel>
              <Textarea value={form.description} onChange={set('description')} rows={7} fontSize="sm" maxLength={5000} />
            </FormControl>

            {checklist.length > 0 && (
              <Box fontSize="sm">
                <HStack spacing={1.5} mb={1}><Icon as={MdPlaylistAddCheck} color="purple.400" /><Text fontWeight="700">Crew checklist</Text></HStack>
                {checklist.map((c) => <Text key={c} color={muted}>• {c}</Text>)}
              </Box>
            )}
            {helpLoading && <HStack fontSize="xs" color={muted}><Spinner size="xs" /><Text>Checking for open jobs on this {domain?.short?.toLowerCase() || 'record'}…</Text></HStack>}

            {problems.length > 0 && (
              <Alert status="warning" borderRadius="lg" fontSize="sm"><AlertIcon /><AlertDescription>{problems.join(' ')}</AlertDescription></Alert>
            )}
            {confirmDuplicate && sameJob && (
              <Alert status="warning" borderRadius="lg" fontSize="sm">
                <Icon as={MdWarning} mr={2} color="orange.400" />
                <AlertDescription>“{sameJob.title}” is already open ({sameJob.status}). Press <b>Create anyway</b> to raise a second one.</AlertDescription>
              </Alert>
            )}
            {error && <Alert status="error" borderRadius="lg" fontSize="sm"><AlertIcon /><AlertDescription>{error}</AlertDescription></Alert>}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} flexWrap="wrap">
          {onOpenFullForm && <Button variant="ghost" size="sm" mr="auto" rightIcon={<MdOpenInNew />} onClick={() => onOpenFullForm(merged)} isDisabled={busy}>Open full form</Button>}
          <Button variant="outline" onClick={onClose} isDisabled={busy}>Cancel</Button>
          <Button colorScheme={PRIORITY_SCHEME[form.priority] === 'red' ? 'red' : 'purple'} leftIcon={<MdBuild />} onClick={submit} isLoading={busy} loadingText="Creating" isDisabled={problems.length > 0}>
            {confirmDuplicate && sameJob ? 'Create anyway' : 'Create work order'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default memo(QuickWorkOrderModal);
